import { Radio } from 'antd';
import type { RadioGroupProps } from 'antd';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';

/**
 * Props {@link RadioGroupBox}.
 */
export interface RadioGroupBoxProps extends Omit<RadioGroupProps, 'size'> {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu. */
  size?: RadioGroupProps['size'];
}

/**
 * Radio group — wrapper `Antd Radio.Group`.
 *
 * Sifat universal:
 * - Density mengikuti `context`.
 * - Class `booku-control` → vertical center alignment + spacing antar Radio
 *   seragam (default Antd margin-right 8px diganti dengan flex `gap: 12px`).
 * - Item Radio individual dapat ditulis sebagai children atau via `options`.
 *
 * Pemakaian:
 * ```tsx
 * <RadioGroupBox value={jenis} onChange={(e) => setJenis(e.target.value)}>
 *   <Radio value="hari">Hari</Radio>
 *   <Radio value="tanggal">Tanggal</Radio>
 * </RadioGroupBox>
 *
 * // Atau via options:
 * <RadioGroupBox
 *   value={jenis}
 *   onChange={(e) => setJenis(e.target.value)}
 *   options={[
 *     { label: 'Hari', value: 'hari' },
 *     { label: 'Tanggal', value: 'tanggal' },
 *   ]}
 * />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
export const RadioGroupBox = forwardRef<HTMLDivElement, RadioGroupBoxProps>(
  function RadioGroupBox(
    { context = 'form', size, className, ...rest },
    ref
  ) {
    const resolvedSize = size ?? controlSizeForContext(context);
    return (
      <Radio.Group
        ref={ref}
        size={resolvedSize}
        className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
        {...rest}
      />
    );
  }
);

// Re-export Radio supaya call site cukup import dari controls/.
export { Radio } from 'antd';
